import { useCallback, useState, useTransition } from "react";
import api from "@/lib/api";

// Module-level frozen empty array. Reusing it for "reset to empty" avoids
// creating a fresh `[]` on every aircraft change, which would break the
// memo barrier of any downstream consumer comparing arrays by identity.
const EMPTY_ARR = Object.freeze([]);
const EMPTY_OBJ = Object.freeze({});

// Encapsulates the entire "user just picked a different aircraft" cascade.
// Returns a single `onAircraftChange(id)` callback plus the cascading state
// (owners, selected owner, crew, temp crew, owner-passenger cache, crew
// passport map) so NewTripModal doesn't have to wire 7 useStates by hand.
//
// `setLegs` must be the modal's existing setter — the ETA recompute uses a
// FUNCTIONAL setLegs(prev => ...) so changing aircraft mid-typing reads the
// latest legs snapshot (fixes a stale-closure where the recompute used
// whatever `legs` was when onAircraftChange was first defined).
//
// `onPassengerSetChanged` is invoked any time the owner-passenger pool
// changes; the modal uses it to reset `docsConfirmedRef` so the docs
// precheck warning re-arms whenever the underlying passengers shift.
export default function useAircraftChange({ setLegs, calculateETA, onPassengerSetChanged }) {
  const [aircraftId, setAircraftId] = useState("");
  const [aircraftOwners, setAircraftOwners] = useState(EMPTY_ARR);
  const [selectedOwner, setSelectedOwnerState] = useState("");
  const [assignedCrew, setAssignedCrew] = useState(EMPTY_ARR);
  // Bug 11 audit: antes el error de carga del crew se silenciaba en el catch
  // y la UI mostraba "Sin tripulación asignada" como si fuera dato. Ahora
  // exponemos el error y la UI lo distingue de "realmente vacío".
  const [crewLoadError, setCrewLoadError] = useState(false);
  const [temporaryCrew, setTemporaryCrew] = useState(EMPTY_ARR);
  const [ownerPassengers, setOwnerPassengers] = useState(EMPTY_OBJ);
  const [crewPassportMap, setCrewPassportMap] = useState(EMPTY_OBJ);
  const [, startTransition] = useTransition();

  const loadOwnerPassengers = useCallback(
    async (ownerId) => {
      if (!ownerId) return;
      // Use a functional setter so the cache-hit guard reads the latest map,
      // not whatever the closure captured at hook construction.
      let alreadyLoaded = false;
      setOwnerPassengers((prev) => {
        if (prev[ownerId]) alreadyLoaded = true;
        return prev;
      });
      if (alreadyLoaded) return;
      try {
        const res = await api.get(`/owners/${ownerId}/passengers`);
        setOwnerPassengers((prev) => ({ ...prev, [ownerId]: res.data || [] }));
        onPassengerSetChanged?.();
      } catch (err) {
        console.error("Failed to load passengers", err);
      }
    },
    [onPassengerSetChanged]
  );

  const setSelectedOwner = useCallback(
    (ownerId) => {
      setSelectedOwnerState(ownerId);
      // Owner change = different passenger pool. Re-arm the docs warning.
      onPassengerSetChanged?.();
    },
    [onPassengerSetChanged]
  );

  const onAircraftChange = useCallback(
    (id) => {
      startTransition(() => {
        setAircraftId(id);
        setAircraftOwners(EMPTY_ARR);
        setAssignedCrew(EMPTY_ARR);
        setTemporaryCrew(EMPTY_ARR);
        setSelectedOwnerState("");
        if (!id) {
          setLegs((prev) =>
            prev.map((leg) => ({
              ...leg,
              arrival_date: "",
              arrival_time: "",
              ete_formatted: "",
            }))
          );
        }
      });
      // Aircraft change = potential passenger set change once a new owner
      // auto-selects, plus expired-docs may differ. Re-arm the warning.
      onPassengerSetChanged?.();
      if (!id) return;

      (async () => {
        setCrewLoadError(false);
        try {
          const [ownersRes, crewRes, tempCrewRes] = await Promise.all([
            api.get(`/aircraft/${id}/owners`),
            api.get(`/aircraft/${id}/crew`),
            api.get(`/aircraft/${id}/temporary-crew`).catch(() => ({ data: [] })),
          ]);
          setAircraftOwners(ownersRes.data || []);
          setTemporaryCrew(tempCrewRes.data || []);
          const crew = crewRes.data || [];
          setAssignedCrew(crew);
          if (ownersRes.data?.length === 1) {
            setSelectedOwnerState(ownersRes.data[0].id);
            loadOwnerPassengers(ownersRes.data[0].id);
          }

          Promise.all(
            crew.map((c) =>
              api
                .get(`/staff/${c.id}/documents`)
                .then((r) => {
                  const passport = (r.data || []).find(
                    (d) => d.doc_type === "passport" && d.expiration_date
                  );
                  return [c.id, passport ? passport.expiration_date : null];
                })
                .catch(() => [c.id, null])
            )
          ).then((results) => {
            startTransition(() => setCrewPassportMap(Object.fromEntries(results)));
          });

          // Functional setLegs so the recompute reads the LATEST snapshot
          // (not the closure's stale `legs`). Without this, typing in a
          // leg while a previous aircraft-change is still resolving would
          // lose the user's keystrokes.
          setLegs((prevLegs) => {
            Promise.all(
              prevLegs.map((leg, i) =>
                leg.origin && leg.destination && leg.departure_date && !leg.time_tbd
                  ? calculateETA(
                      leg.origin,
                      leg.destination,
                      leg.departure_date,
                      leg.departure_time,
                      id
                    ).then((eta) => [i, eta])
                  : Promise.resolve([i, null])
              )
            ).then((etaResults) => {
              startTransition(() => {
                setLegs((current) => {
                  const updated = [...current];
                  etaResults.forEach(([i, eta]) => {
                    if (eta && (eta.ete_formatted || eta.arrival_date))
                      updated[i] = { ...updated[i], ...eta };
                  });
                  return updated;
                });
              });
            });
            return prevLegs;
          });
        } catch (err) {
          console.error("Failed to load owners/crew", err);
          setCrewLoadError(true);
        }
      })();
    },
    [setLegs, calculateETA, loadOwnerPassengers, onPassengerSetChanged]
  );

  return {
    aircraftId,
    setAircraftId,
    aircraftOwners,
    selectedOwner,
    setSelectedOwner,
    assignedCrew,
    crewLoadError,
    temporaryCrew,
    ownerPassengers,
    crewPassportMap,
    onAircraftChange,
    loadOwnerPassengers,
  };
}
